import React, { useState, useEffect } from 'react';
import { ref, get, push, set, update, remove } from 'firebase/database';
import { database } from '../firebase/config';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import PulsantiCondivisionePDF from './PulsantiCondivisionePDF';
import '../styles/Liste.css';
import { formattaData } from '../utils/dateUtils'; 
import { useFirebaseData } from '../hooks/useFirebaseData'; 

// Stili per il PDF del report cantiere
const pdfStyles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica'
  },
  header: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: 'center',
    color: '#2c3e50',
    fontWeight: 'bold'
  },
  subheader: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: 'center',
    color: '#7f8c8d'
  },
  infoBox: {
    backgroundColor: '#e3f2fd',
    padding: 10,
    marginBottom: 15,
    borderRadius: 5
  },
  infoText: {
    fontSize: 11,
    textAlign: 'center',
    fontWeight: 'bold'
  },
  table: {
    display: 'table',
    width: '100%',
    marginTop: 10
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
    minHeight: 25,
    alignItems: 'center'
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    fontWeight: 'bold',
    fontSize: 9
  },
  tableCol: {
    padding: 5,
    fontSize: 8
  },
  col1: { width: '10%' },
  col2: { width: '14%' },
  col3: { width: '14%' },
  col4: { width: '8%' },
  col5: { width: '8%' },
  col6: { width: '8%' },
  col7: { width: '14%' },
  col8: { width: '24%' },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    color: '#95a5a6'
  }
});


function ListaCantieri() {
  const { dipendenti, veicoli, clienti,
    caricaDipendenti, caricaVeicoli, caricaClienti
  } = useFirebaseData();
  const [cantieri, setCantieri] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    clienteId: '',
    luogo: ''
  });

  const [showReportModal, setShowReportModal] = useState(false);
  const [cantiereSelezionato, setCantiereSelezionato] = useState(null);
  const [programmiCantiere, setProgrammiCantiere] = useState([]);
  const [ordinamentoReport, setOrdinamentoReport] = useState({
    campo: 'data',
    direzione: 'desc'
  });


  useEffect(() => {
    caricaClienti();
    caricaCantieri();
  }, []);


  async function caricaCantieri() {
    const cantieriRef = ref(database, 'cantieri');
    const snapshot = await get(cantieriRef);
    
    if (snapshot.exists()) {
      const data = snapshot.val();
      const cantieriArray = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      setCantieri(cantieriArray);
    } else {
      setCantieri([]);
    }
  }

  function apriModal(cantiere = null) {
    if (cantiere) {
      setEditingId(cantiere.id);
      setFormData({
        clienteId: cantiere.clienteId,
        luogo: cantiere.luogo
      });
    } else {
      setEditingId(null);
      setFormData({ clienteId: '', luogo: '' });
    }
    setShowModal(true);
  }

  function chiudiModal() {
    setShowModal(false);
    setEditingId(null);
    setFormData({ clienteId: '', luogo: '' });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.clienteId || !formData.luogo) {
      alert('Compila tutti i campi');
      return;
    }

    try {
      if (editingId) {
        const cantiereRef = ref(database, `cantieri/${editingId}`);
        await update(cantiereRef, {
          clienteId: formData.clienteId,
          luogo: formData.luogo
        });
        alert('Cantiere modificato con successo!');
      } else {
        const cantieriRef = ref(database, 'cantieri');
        const newCantiereRef = push(cantieriRef);
        await set(newCantiereRef, {
          clienteId: formData.clienteId,
          luogo: formData.luogo,
          createdAt: Date.now()
        });
        alert('Cantiere creato con successo!');
      }
      
      chiudiModal();
      caricaCantieri();
    } catch (error) {
      alert('Errore: ' + error.message);
    }
  }

  async function eliminaCantiere(id, luogo) {
    if (window.confirm(`Sei sicuro di voler eliminare il cantiere "${luogo}"?`)) {
      try {
        const cantiereRef = ref(database, `cantieri/${id}`);
        await remove(cantiereRef);
        alert('Cantiere eliminato!');
        caricaCantieri();
      } catch (error) {
        alert('Errore: ' + error.message);
      }
    }
  }

  function getNomeCliente(clienteId) {
    const cliente = clienti.find(c => c.id === clienteId);
    return cliente ? cliente.ragioneSociale : 'Cliente non trovato';
  }

  function getCliente(clienteId) {
    return clienti.find(c => c.id === clienteId);
  }


  async function apriReportCantiere(cantiere) {
    setCantiereSelezionato(cantiere);
    setShowReportModal(true);
    
    await Promise.all([
      caricaProgrammiCantiere(cantiere.id),
      caricaDipendenti(),
      caricaVeicoli()
    ]);
  }

  async function caricaProgrammiCantiere(cantiereId) {
    try {
      const programmiRef = ref(database, 'programmi-lavorativi');
      const snapshot = await get(programmiRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const programmiArray = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .filter(prog => prog.cantiereId === cantiereId)
          .sort((a, b) => new Date(b.data) - new Date(a.data));
        
        setProgrammiCantiere(programmiArray);
      } else {
        setProgrammiCantiere([]);
      }
    } catch (error) {
      console.error('Errore caricamento programmi:', error);
      setProgrammiCantiere([]);
    }
  }


  function chiudiReportModal() {
    setShowReportModal(false);
    setCantiereSelezionato(null);
    setProgrammiCantiere([]);
  }

  function getDipendente(id) {
    return dipendenti.find(d => d.id === id);
  }

  function getVeicolo(id) {
    return veicoli.find(v => v.id === id);
  }

  function handleOrdinamentoReport(campo) {
    let direzione = 'asc';
    
    if (ordinamentoReport.campo === campo && ordinamentoReport.direzione === 'asc') {
      direzione = 'desc';
    }
    
    setOrdinamentoReport({ campo, direzione });
  }

  function getProgrammiOrdinati() {
    const programmiCopia = [...programmiCantiere];
    
    programmiCopia.sort((a, b) => {
      let valoreA, valoreB;
      
      if (ordinamentoReport.campo === 'data') {
        valoreA = new Date(a.data);
        valoreB = new Date(b.data);
      } else if (ordinamentoReport.campo === 'dipendente') {
        const dipA = getDipendente(a.dipendenteId);
        const dipB = getDipendente(b.dipendenteId);
        valoreA = dipA ? dipA.cognome.toLowerCase() : '';
        valoreB = dipB ? dipB.cognome.toLowerCase() : '';
      } else if (ordinamentoReport.campo === 'veicolo') {
        const veicA = getVeicolo(a.veicoloId);
        const veicB = getVeicolo(b.veicoloId);
        valoreA = veicA ? `${veicA.marca} ${veicA.modello}`.toLowerCase() : '';
        valoreB = veicB ? `${veicB.marca} ${veicB.modello}`.toLowerCase() : '';
      }
      
      if (valoreA < valoreB) {
        return ordinamentoReport.direzione === 'asc' ? -1 : 1;
      }
      if (valoreA > valoreB) {
        return ordinamentoReport.direzione === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return programmiCopia;
  }

  function getFrecciaReport(campo) {
    if (ordinamentoReport.campo !== campo) {
      return '↕️';
    }
    return ordinamentoReport.direzione === 'asc' ? '🔼' : '🔽';
  }

  const ReportCantierePDF = ({ cantiere, cliente, programmi }) => (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <Text style={pdfStyles.header}>Report cantiere</Text>
        <Text style={pdfStyles.subheader}>
          {cantiere.luogo} - Cliente: {cliente ? cliente.ragioneSociale : 'N/D'}
        </Text>
        
        <View style={pdfStyles.infoBox}>
          <Text style={pdfStyles.infoText}>
            Totale Programmi: {programmi.length}
          </Text>
        </View>

        <View style={pdfStyles.table}>
          <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
            <Text style={[pdfStyles.tableCol, pdfStyles.col1]}>Data</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col2]}>Dipendente</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col3]}>Veicolo</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col4]}>Inizio</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col5]}>Fine</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col6]}>Durata</Text>
            <Text style={[pdfStyles.tableCol, pdfStyles.col8]}>Note</Text>
          </View>

          {programmi.map((prog, index) => {
            const dipendente = getDipendente(prog.dipendenteId);
            const veicolo = getVeicolo(prog.veicoloId);

            return (
              <View key={index} style={pdfStyles.tableRow}>
                <Text style={[pdfStyles.tableCol, pdfStyles.col1]}>{formattaData(prog.data)}</Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col2]}>
                  {dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}
                </Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col3]}>
                  {veicolo ? `${veicolo.marca} ${veicolo.modello}` : 'N/D'}
                </Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col4]}>{prog.orarioInizio}</Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col5]}>{prog.orarioFine}</Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col6]}>{prog.durataLavoro}</Text>
                <Text style={[pdfStyles.tableCol, pdfStyles.col8]}>
                  {prog.note || '-'}
                </Text>
              </View>
            );
          })}
        </View>

        <Text style={pdfStyles.footer}>
          Report generato il {new Date().toLocaleDateString('it-IT')} alle {new Date().toLocaleTimeString('it-IT')}
        </Text>
      </Page>
    </Document>
  );

  // Componente principale ListaCantieri
  return (

    <div className="lista-container">
      <div className="lista-header">
        <h2>🏗️ Gestione Cantieri</h2>
        <button className="btn-add" onClick={() => apriModal()}>
          + Aggiungi Cantiere
        </button>
      </div>

      {clienti.length === 0 ? (
        <div className="empty-state">
          <p>⚠️ Prima di creare cantieri, devi creare almeno un cliente!</p>
          <p>Vai nella sezione "Clienti" per aggiungerne uno.</p>
        </div>
      ) : cantieri.length === 0 ? (
        <div className="empty-state">
          <p>Nessun cantiere presente.</p>
          <p>Clicca su "Aggiungi Cantiere" per iniziare.</p>
        </div>
      ) : (
        <>
          {/* Tabella Desktop */}
          <table className="data-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Luogo</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {cantieri.map(cantiere => {
                const cliente = clienti.find(c => c.id === cantiere.clienteId);
                
                return (
                  <tr key={cantiere.id}>
                    <td>{getNomeCliente(cantiere.clienteId)}</td>
                    <td>{cantiere.luogo}</td>
                    <td className="actions">
                      <button 
                        className="btn-edit"
                        onClick={() => apriReportCantiere(cantiere)}
                        style={{background: '#9b59b6'}}
                      >
                        <img src="/icon-report.svg" alt="pdf" style={{ width: '40px' }} />
                      </button>
                      <button 
                        className="btn-edit"
                        onClick={() => apriModal(cantiere)}
                      >
                        <img src="/icon-edit.svg" alt="pdf" style={{ width: '40px' }} />

                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => eliminaCantiere(cantiere.id, cantiere.luogo)}
                      >
                        <img src="/icon-delete.svg" alt="pdf" style={{ width: '40px' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Layout Card Mobile */}
          <div className="mobile-cards">
            {cantieri.map(cantiere => (
              <div key={cantiere.id} className="mobile-card">
                <div className="mobile-card-header">
                  🏗️ {cantiere.luogo}
                </div>
                
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Cliente:</span>
                  <span className="mobile-card-value">{getNomeCliente(cantiere.clienteId)}</span>
                </div>
                
                <div className="mobile-card-actions">
                  <button 
                    className="btn-edit"
                    onClick={() => apriReportCantiere(cantiere)}
                    style={{background: '#9b59b6'}}
                  >
                    <img src="/icon-report.svg" alt="pdf" style={{ width: '40px' }} />
                  </button>
                  <button 
                    className="btn-edit"
                    onClick={() => apriModal(cantiere)}
                  >
                    <img src="/icon-edit.svg" alt="pdf" style={{ width: '40px' }} />
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => eliminaCantiere(cantiere.id, cantiere.luogo)}
                  >
                    <img src="/icon-delete.svg" alt="pdf" style={{ width: '40px' }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}



      {showModal && (
        <div className="modal-overlay" onClick={chiudiModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Modifica Cantiere' : 'Nuovo Cantiere'}</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Cliente *</label>
                <select
                  value={formData.clienteId}
                  onChange={(e) => setFormData({...formData, clienteId: e.target.value})}
                  required
                >
                  <option value="">-- Seleziona un cliente --</option>
                  {clienti.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.ragioneSociale}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Luogo *</label>
                <input
                  type="text"
                  value={formData.luogo}
                  onChange={(e) => setFormData({...formData, luogo: e.target.value})}
                  placeholder="es. Via Roma 123, Milano"
                  required
                />
              </div>

              <div className="modal-buttons">
                <button type="button" className="btn-cancel" onClick={chiudiModal}>
                  Annulla
                </button>
                <button type="submit" className="btn-save">
                  {editingId ? 'Salva Modifiche' : 'Crea Cantiere'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Report Cantiere */}
      {showReportModal && cantiereSelezionato && (
        <div className="modal-overlay" onClick={chiudiReportModal}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{maxWidth: '90%', maxHeight: '90vh', overflow: 'auto'}}
          >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
              <div>
                <h3 style={{margin: '0 0 5px 0'}}>📊 Report cantiere - {cantiereSelezionato.luogo}</h3>
                <p style={{margin: 0, color: '#666', fontSize: '14px'}}>
                  Cliente: {getCliente(cantiereSelezionato.clienteId)?.ragioneSociale || 'N/D'}
                </p>
              </div>
              <button 
                onClick={chiudiReportModal}
                style={{
                  background: '#95a5a6',
                  border: 'none',
                  color: 'white',
                  padding: '8px 15px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                ✕
              </button>
            </div>

            {programmiCantiere.length === 0 ? (
              <div style={{textAlign: 'center', padding: '40px', color: '#7f8c8d'}}>
                <p>Nessun programma lavorativo trovato per questo cantiere.</p>
              </div>
            ) : (
              <>
                {/* 👇 Riga gialla con bottone PDF */}
                <div style={{
                  background: '#efd400',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  <strong>Totale programmi: {programmiCantiere.length}</strong>
                  
                  <PulsantiCondivisionePDF
                    documentoPDF={<ReportCantierePDF cantiere={cantiereSelezionato} cliente={getCliente(cantiereSelezionato.clienteId)} programmi={getProgrammiOrdinati()} />}
                    nomeFile={`Report_Cantiere_${cantiereSelezionato.luogo.replace(/ /g, '_')}.pdf`}
                    testoCondivisione={`📊 Report Cantiere\n\n🏗️ ${cantiereSelezionato.luogo}\n🏢 ${getCliente(cantiereSelezionato.clienteId)?.ragioneSociale || 'N/D'}\n📋 ${programmiCantiere.length} programmi totali`}
                    emailDestinatario=""
                    oggettoEmail={`Report Cantiere - ${cantiereSelezionato.luogo}`}
                    corpoEmail={`Buongiorno,\n\nallego il report completo dei lavori svolti nel cantiere:\n\nCANTIERE: ${cantiereSelezionato.luogo}\nCLIENTE: ${getCliente(cantiereSelezionato.clienteId)?.ragioneSociale || 'N/D'}\nTOTALE PROGRAMMI: ${programmiCantiere.length}\n\nCordiali saluti`}
                  />
                </div>

                {/* 👇 TABELLA DESKTOP (nascosta su mobile) */}
                <div style={{overflowX: 'auto'}} className="desktop-only">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th 
                          onClick={() => handleOrdinamentoReport('data')}
                          style={{cursor: 'pointer', userSelect: 'none'}}
                        >
                          Data {getFrecciaReport('data')}
                        </th>
                        <th 
                          onClick={() => handleOrdinamentoReport('dipendente')}
                          style={{cursor: 'pointer', userSelect: 'none'}}
                        >
                          Dipendente {getFrecciaReport('dipendente')}
                        </th>
                        <th 
                          onClick={() => handleOrdinamentoReport('veicolo')}
                          style={{cursor: 'pointer', userSelect: 'none'}}
                        >
                          Veicolo {getFrecciaReport('veicolo')}
                        </th>
                        <th>Orario Inizio</th>
                        <th>Orario Fine</th>
                        <th>Durata</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getProgrammiOrdinati().map(prog => {
                        const dipendente = getDipendente(prog.dipendenteId);
                        const veicolo = getVeicolo(prog.veicoloId);

                        return (
                          <tr key={prog.id}>
                            <td><strong>{formattaData(prog.data)}</strong></td>
                            <td>
                              {dipendente ? (
                                <>
                                  {dipendente.nome} {dipendente.cognome}
                                </>
                              ) : 'N/D'}
                            </td>
                            <td>
                              {veicolo ? (
                                <>
                                  {veicolo.marca} {veicolo.modello}
                                  <br/>
                                  <small style={{color: '#666'}}>({veicolo.targa})</small>
                                </>
                              ) : 'N/D'}
                            </td>
                            <td>{prog.orarioInizio}</td>
                            <td>{prog.orarioFine}</td>
                            <td><strong>{prog.durataLavoro}</strong></td>
                            <td style={{maxWidth: '200px', fontSize: '12px'}}>
                              {prog.note || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* 👇 LAYOUT CARD MOBILE (nascosto su desktop) */}
                <div className="mobile-cards">
                  {getProgrammiOrdinati().map(prog => {
                    const dipendente = getDipendente(prog.dipendenteId);
                    const veicolo = getVeicolo(prog.veicoloId);

                    return (
                      <div key={prog.id} className="mobile-card">
                        <div className="mobile-card-header">
                          📅 {formattaData(prog.data)}
                        </div>
                        
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">👤 Dipendente:</span>
                          <span className="mobile-card-value">
                            {dipendente ? `${dipendente.nome} ${dipendente.cognome}` : 'N/D'}
                          </span>
                        </div>

                        <div className="mobile-card-row">
                          <span className="mobile-card-label">🚗 Veicolo:</span>
                          <span className="mobile-card-value">
                            {veicolo ? `${veicolo.marca} ${veicolo.modello} (${veicolo.targa})` : 'N/D'}
                          </span>
                        </div>

                        <div className="mobile-card-row">
                          <span className="mobile-card-label">🕐 Orario:</span>
                          <span className="mobile-card-value">
                            {prog.orarioInizio} - {prog.orarioFine}
                          </span>
                        </div>

                        <div className="mobile-card-row">
                          <span className="mobile-card-label">⏱️ Durata:</span>
                          <span className="mobile-card-value">
                            <strong>{prog.durataLavoro}</strong>
                          </span>
                        </div>

                        {prog.note && (
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">📝 Note:</span>
                            <span className="mobile-card-value" style={{fontSize: '12px'}}>
                              {prog.note}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ListaCantieri;
